/**
 * Remove Ads — the one in-app purchase this build has.
 *
 * ❓ PLACEHOLDER — none of this exists in the decomp. The shipped game buys
 * through an in-process billing SDK; a web port has no store at all, so every
 * call here is a question asked of the wrapper app. Shape copied from
 * `reference/DiceFlow/index.html` (a shipped port of the same house), spec in
 * `web/docs/ADS-MONETIZATION.md` §A.2.4. The product id and the fallback price
 * are **product decisions** of 2026-09-04 — do not attach an RVA to them.
 *
 * Wire protocol, all four request/reply pairs taken from DiceFlow verbatim:
 *
 *   out `get_remove_ads_status` → in `remove_ads_status` { purchased }
 *   out `get_products`          → in `products`          { products: [{ productId, price }] }
 *   out `purchase_remove_ads`   → in `purchase_result`   { result, adsRemoved }
 *   out `restore_purchases`     → in `restore_result`    { purchased }
 *
 * **Restore Purchases is mandatory**, not a nicety: `remove_ads` is a
 * non-consumable, and Google Play requires a way for a player who already owns
 * it — new device, reinstall, cleared data — to get it back without paying
 * twice. A build that can only buy would fail policy review.
 *
 * The entitlement moves from three sources — a purchase, a restore, and an
 * unsolicited `remove_ads_changed` push from the app (a purchase made on
 * another device). All three land in `setAdsRemoved`, which is the only place
 * that writes the flag, persists it, and notifies listeners.
 */

import { nativeBridge, type NativeBridge } from './bridge'
import { monetizationAnalytics, type MonetizationAnalytics } from './analytics'

/** ❓ Product decision, spec 2026-09-04. Must match the store listing exactly. */
export const REMOVE_ADS_PRODUCT = 'remove_ads'

/**
 * ❓ Product decision, spec 2026-09-04. Shown only until the store answers —
 * a real price is localised and set in the Play console, so this string is a
 * placeholder for the gap before `products` comes back, never the truth.
 */
export const REMOVE_ADS_FALLBACK_PRICE = '$4.99'

/** The four verdicts the billing flow can end in (DiceFlow uses the same set). */
export type PurchaseResult = 'success' | 'cancelled' | 'pending' | 'failed'

export interface StoreProduct {
  productId: string
  price: string
}

/**
 * `adsRemoved` is the only monetization state worth keeping across sessions,
 * and it deliberately does **not** live in `LocalSaveData`: that file is a 1:1
 * port of `LocalSaveData..ctor` and has exactly three fields. See docs §B.2.
 */
const STORAGE_KEY = 'bus-traffic-loop.monetization'

interface StoredMonetization {
  adsRemoved?: boolean
}

function readStored(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return false
    const parsed = JSON.parse(raw) as StoredMonetization | null
    return parsed?.adsRemoved === true
  } catch {
    // Private mode, disabled storage, or a corrupt blob. Losing the flag here
    // is recoverable — `refreshEntitlement` re-asks the store on boot.
    return false
  }
}

function writeStored(adsRemoved: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ adsRemoved } satisfies StoredMonetization))
  } catch {
    // Nothing to do: the flag still holds for this session.
  }
}

/** The reply is app-supplied, so nothing about its shape is assumed. */
function parseProducts(value: unknown): StoreProduct[] {
  if (!Array.isArray(value)) return []
  const products: StoreProduct[] = []
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue
    const { productId, price } = entry as Partial<StoreProduct>
    if (typeof productId !== 'string' || typeof price !== 'string' || price === '') continue
    products.push({ productId, price })
  }
  return products
}

const PURCHASE_RESULTS: readonly PurchaseResult[] = ['success', 'cancelled', 'pending', 'failed']

function parseResult(value: unknown): PurchaseResult {
  return PURCHASE_RESULTS.includes(value as PurchaseResult) ? (value as PurchaseResult) : 'failed'
}

export class IapService {
  private removed = readStored()
  private readonly listeners: ((removed: boolean) => void)[] = []

  /** `price()` asks the store once; every later call reads this. */
  private pricePromise: Promise<string> | null = null

  /**
   * Double-tap guards. A second tap while the store is open returns the very
   * same promise instead of opening a second billing flow — the popup shows a
   * modal "talking to the store" overlay meanwhile (docs §A.2.4), but the guard
   * must hold even if that overlay is ever removed.
   */
  private buyPromise: Promise<PurchaseResult> | null = null
  private restorePromise: Promise<boolean> | null = null

  constructor(
    private readonly bridge: NativeBridge = nativeBridge,
    private readonly analytics: MonetizationAnalytics = monetizationAnalytics,
  ) {
    // Source three: the app telling us on its own that the entitlement moved.
    this.bridge.onRemoveAdsChanged((purchased) => this.setAdsRemoved(purchased))
  }

  get adsRemoved(): boolean {
    return this.removed
  }

  /**
   * Listeners fire on every change, not only on a purchase: the header no-ads
   * button and the popup's `.owned` state both follow this.
   */
  onAdsRemovedChanged(listener: (removed: boolean) => void): void {
    this.listeners.push(listener)
  }

  /**
   * Ask the store what the player already owns. Called at boot, so a reinstall
   * that lost `localStorage` still comes back entitled.
   *
   * A `false` answer does **not** clear a stored `true`: only a purchase, a
   * restore, or an explicit `remove_ads_changed` push moves the flag, and the
   * offline fallback is `false` for everyone in a plain browser. Revoking an
   * entitlement because the wrapper is missing would show ads to a payer.
   */
  async refreshEntitlement(): Promise<boolean> {
    const reply = await this.bridge.request(
      'get_remove_ads_status',
      'ras',
      {},
      // Offline: a browser has no store, so nothing has been bought here.
      { purchased: false },
    )
    if (reply.purchased === true) this.setAdsRemoved(true)
    return this.removed
  }

  /**
   * The localised price for the button label. Asked once and cached — the price
   * cannot change mid-session, and the popup may open many times.
   */
  price(): Promise<string> {
    if (this.pricePromise) return this.pricePromise
    this.pricePromise = this.bridge
      .request(
        'get_products',
        'gp',
        { productIds: [REMOVE_ADS_PRODUCT] },
        // Offline: no store to list, so an empty set — the caller falls back.
        { products: [] },
      )
      .then((reply) => {
        const product = parseProducts(reply.products).find(
          (candidate) => candidate.productId === REMOVE_ADS_PRODUCT,
        )
        return product?.price ?? REMOVE_ADS_FALLBACK_PRICE
      })
      .catch(() => REMOVE_ADS_FALLBACK_PRICE)
    return this.pricePromise
  }

  /**
   * Open the billing flow. Resolves with the store's verdict and never throws,
   * so a button handler can `await` it and then re-render.
   *
   * `remove_ads_open` is the popup's event, not ours — it fires when the popup
   * opens, which is a different moment from pressing Buy. Logging it here too
   * would double-count the funnel's first step.
   */
  buyRemoveAds(level: number): Promise<PurchaseResult> {
    if (this.buyPromise) return this.buyPromise
    this.analytics.log('remove_ads_start', level)
    this.buyPromise = this.bridge
      .request(
        'purchase_remove_ads',
        'pra',
        { productId: REMOVE_ADS_PRODUCT },
        // Offline: a browser must never grant the entitlement for free.
        { result: 'failed', adsRemoved: false },
      )
      .then((reply) => {
        const result = parseResult(reply.result)
        // Trust either signal: some wrappers answer `success` without the flag,
        // others push the flag first. `pending` (slow card, parental approval)
        // is not an entitlement — the app sends `remove_ads_changed` later.
        if (result === 'success' || reply.adsRemoved === true) this.setAdsRemoved(true)
        this.analytics.log('remove_ads_result', level, { result })
        return result
      })
      .catch(() => {
        this.analytics.log('remove_ads_result', level, { result: 'failed' })
        return 'failed' as PurchaseResult
      })
      .finally(() => {
        this.buyPromise = null
      })
    return this.buyPromise
  }

  /**
   * Restore a non-consumable already owned by this Play account. Mandatory for
   * store policy; see the file header.
   */
  restore(level: number): Promise<boolean> {
    if (this.restorePromise) return this.restorePromise
    this.restorePromise = this.bridge
      .request(
        'restore_purchases',
        'rst',
        {},
        // Offline: nothing to restore, and no way to verify a claim.
        { purchased: false },
      )
      .then((reply) => reply.purchased === true)
      .catch(() => false)
      .then((restored) => {
        if (restored) this.setAdsRemoved(true)
        this.analytics.log('remove_ads_restore', level, { restored })
        return restored
      })
      .finally(() => {
        this.restorePromise = null
      })
    return this.restorePromise
  }

  /**
   * The single writer of the entitlement — purchase, restore and the app's push
   * all land here, so persistence and notification cannot drift apart.
   */
  private setAdsRemoved(removed: boolean): void {
    if (removed === this.removed) return
    this.removed = removed
    writeStored(removed)
    for (const listener of this.listeners) listener(removed)
  }
}

/** The service this build actually uses. */
export const iapService = new IapService()
